import { useCallback, useEffect, useRef } from 'react';

/** Тип, описывающий произвольную функцию */
type AnyFn = (...args: any[]) => any;

type Debounced<Callback extends AnyFn> = (...args: Parameters<Callback>) => void;

/**
 * Возвращает функцию, которая вызовет переданный колбэк,
 * только если прошло `waitMs` миллисекунд с момента последнего вызова.
 * Хук гарантирует, что колбэк не будет вызван после анмаунта компонента.
 *
 * @param callback Колбэк
 * @param waitMs Время ожидания вызова колбэка
 * @typeParam Callback Тип колбэка
 * @returns Функция, вызывающая колбэк только спустя `waitMs` миллисекунд
 *          с момента последнего вызова полученной функции
 */
export function useDebouncedFunction<Callback extends AnyFn>(callback: Callback, waitMs: number): Debounced<Callback> {
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  return useCallback(
    (...args: Parameters<Callback>) => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => {
        callback(...args);
      }, waitMs);
    },
    [callback, waitMs]
  );
}
